import React, {useState, useRef} from 'react';
import {Box, styled} from '@mui/material';
import {
  designSystemToken,
  Headline,
  Icon,
  IconName,
} from '@lightricks/react-design-system';
import HoverableVideo from '@contentlab/components/hoverable-video';

const FOOTER_HEIGHT = 60;

const shouldNotForwardProps = ['cardHeight'];

const FeatureCardContainer = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{cardHeight?: number}>(({cardHeight = 246}) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 0',
  width: '100%',
  minWidth: '219px',
  height: `${cardHeight}px`,
  borderRadius: '16px',
  backgroundColor: designSystemToken('semantic.bg.secondary'),
  border: `1px solid ${designSystemToken('semantic.bg.tertiary')}`,
  boxShadow:
    '0px 0px 1px 0px rgba(14, 21, 38, 0.08), 0px 2px 32px 0px rgba(14, 21, 38, 0.06)',
  cursor: 'pointer',
  overflow: 'hidden',
}));

const FooterContainer = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  padding: '0 16px',
  justifyContent: 'space-between',
  height: `${FOOTER_HEIGHT}px`,
  backgroundColor: designSystemToken('semantic.bg.secondary'),
}));

const IconContainer = styled(Box)(() => ({
  position: 'relative',
  backgroundColor: designSystemToken('semantic.bg.tertiary'),
  display: 'flex',
  width: '40px',
  height: '40px',
  borderRadius: '12px',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
}));

const AnimatedIconWrapper = styled('div')<{
  state: 'center' | 'enter' | 'exit';
}>(({state}) => {
  let transform = 'translateX(0)';
  let opacity = 1;
  if (state === 'enter') {
    transform = 'translateX(-40px)';
    opacity = 0;
  } else if (state === 'exit') {
    transform = 'translateX(40px)';
    opacity = 0;
  }
  return {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: `transform ${ANIMATION_DURATION / 1000}s ease-in-out, 
                 opacity ${ANIMATION_DURATION / 1000}s ease-in-out`,
    transform,
    opacity,
    pointerEvents: state === 'center' ? 'auto' : 'none',
  };
});

export interface FeatureCardProps {
  testID?: string;
  title: string;
  iconName: IconName;
  videoPath: string;
  thumbnailPath: string;
  onClick?: () => void;
  disabled?: boolean;
  cardHeight?: number;
}

const ANIMATION_DURATION = 300;

function FeatureCard({
  testID = 'feature-card',
  title,
  iconName,
  videoPath,
  thumbnailPath,
  onClick,
  disabled = false,
  cardHeight = 246,
}: FeatureCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevHovered = useRef(isHovered);

  const handleMouseEnter = () => {
    if (!disabled && !isHovered) {
      setIsHovered(true);
      setIsTransitioning(true);
      setTimeout(() => setIsTransitioning(false), ANIMATION_DURATION);
    }
  };

  const handleMouseLeave = () => {
    if (!disabled && isHovered) {
      setIsHovered(false);
      setIsTransitioning(true);
      setTimeout(() => setIsTransitioning(false), ANIMATION_DURATION);
    }
  };

  // Determine animation state for each icon
  let mainState: 'center' | 'enter' | 'exit' = 'center';
  let arrowState: 'center' | 'enter' | 'exit' = 'center';

  if (isTransitioning) {
    if (!prevHovered.current && isHovered) {
      // Transition: main -> arrow (hover in)
      mainState = 'exit';
      arrowState = 'enter';
    } else if (prevHovered.current && !isHovered) {
      // Transition: arrow -> main (hover out)
      mainState = 'enter';
      arrowState = 'exit';
    }
  } else if (isHovered) {
    mainState = 'exit'; // keep hidden
    arrowState = 'center';
  } else {
    mainState = 'center';
    arrowState = 'exit'; // keep hidden
  }

  prevHovered.current = isHovered;

  return (
    <FeatureCardContainer
      data-testid={testID}
      cardHeight={cardHeight}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        filter: disabled ? 'grayscale(100%)' : 'none',
      }}
    >
      <HoverableVideo
        videoUrl={videoPath}
        thumbnailUrl={thumbnailPath}
        width="100%"
        height={`${cardHeight - FOOTER_HEIGHT}px`}
        isHovered={isHovered}
        sx={{
          borderRadius: '16px 16px 0 0',
        }}
      />
      <FooterContainer>
        <Headline size="xxs" color={designSystemToken('semantic.fg.primary')}>
          {title}
        </Headline>
        <IconContainer>
          <AnimatedIconWrapper state={mainState}>
            <Icon name={iconName} size="medium" appearance="neutral" />
          </AnimatedIconWrapper>
          <AnimatedIconWrapper state={arrowState}>
            <Icon
              name="Actions-Arrow-Long-Forward"
              size="medium"
              appearance="neutral"
            />
          </AnimatedIconWrapper>
        </IconContainer>
      </FooterContainer>
    </FeatureCardContainer>
  );
}

export default FeatureCard;
